import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api, money, formatApiError } from "@/lib/api";
import { ArrowLeft, Save, Building2, Percent, Moon, Calendar, Map, TrendingUp } from "lucide-react";
import { toast } from "sonner";

export default function AgreementDetail() {
    const { id } = useParams();
    const [data, setData] = useState(null);
    const [serviceTypes, setServiceTypes] = useState([]);
    const [rates, setRates] = useState({});
    const [params, setParams] = useState({ discount_pct: 0, night_surcharge_pct: 25, holiday_surcharge_pct: 30, km_rate_foreign: 28, sla_minutes: 30, credit_days: 30 });
    const [saving, setSaving] = useState(false);

    const load = async () => {
        try {
            const [a, t] = await Promise.all([api.get(`/agreements/${id}`), api.get("/service-types")]);
            if (a?.data) {
                setData(a.data);
                setRates(a.data.agreement?.rates || {});
                setParams({
                    discount_pct: a.data.agreement?.discount_pct || 0,
                    night_surcharge_pct: a.data.agreement?.night_surcharge_pct || 25,
                    holiday_surcharge_pct: a.data.agreement?.holiday_surcharge_pct || 30,
                    km_rate_foreign: a.data.agreement?.km_rate_foreign || 28,
                    sla_minutes: a.data.agreement?.sla_minutes || 30,
                    credit_days: a.data.agreement?.credit_days || 30,
                });
            }
            if (t?.data) setServiceTypes(t.data);
        } catch (e) { console.warn(e); }
    };

    useEffect(() => { load(); }, [id]);

    const updateRate = (code, val) => {
        const v = parseFloat(val);
        if (isNaN(v) || v <= 0) {
            const copy = { ...rates }; delete copy[code]; setRates(copy);
        } else {
            setRates({ ...rates, [code]: v });
        }
    };

    const save = async () => {
        setSaving(true);
        try {
            await api.patch(`/agreements/${id}/rates`, { rates, ...params });
            toast.success("Tarifario actualizado");
            load();
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail));
        } finally {
            setSaving(false);
        }
    };

    if (!data) return <div className="text-sm text-slate-500 font-mono">Cargando...</div>;
    const { agreement, client, total_revenue, total_cost, total_margin, service_count } = data;
    const marginPct = total_revenue > 0 ? (total_margin / total_revenue * 100) : 0;

    return (
        <div className="space-y-4 max-w-6xl" data-testid="agreement-detail">
            <Link to="/clients" className="text-xs text-slate-500 hover:text-slate-900 flex items-center gap-1">
                <ArrowLeft className="w-3 h-3" /> Clientes
            </Link>

            <div className="fm-card p-5 bg-slate-900 text-white">
                <div className="flex items-start justify-between flex-wrap gap-3">
                    <div className="flex items-start gap-3">
                        <Building2 className="w-7 h-7 text-red-500 mt-1" strokeWidth={1.5} />
                        <div>
                            <div className="text-[10px] uppercase tracking-widest text-red-400 font-bold">Convenio · {agreement.type}</div>
                            <h2 className="font-display font-black text-2xl tracking-tight">{client?.business_name}</h2>
                            <div className="text-xs text-slate-300 mt-1">{agreement.start_date} → {agreement.end_date}</div>
                        </div>
                    </div>
                    <div className="grid grid-cols-3 gap-6 text-right">
                        <div>
                            <div className="text-[9px] uppercase tracking-widest text-slate-400 font-bold">Ingresos</div>
                            <div className="font-mono font-bold text-lg tabular-nums">{money(total_revenue)}</div>
                        </div>
                        <div>
                            <div className="text-[9px] uppercase tracking-widest text-slate-400 font-bold">Margen</div>
                            <div className="font-mono font-bold text-lg tabular-nums text-emerald-400">{money(total_margin)}</div>
                            <div className="text-[10px] text-slate-400">{marginPct.toFixed(1)}%</div>
                        </div>
                        <div>
                            <div className="text-[9px] uppercase tracking-widest text-slate-400 font-bold">Servicios</div>
                            <div className="font-mono font-bold text-lg tabular-nums">{service_count}</div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Surcharges & params */}
            <div className="fm-card p-5">
                <h3 className="font-display font-bold text-base mb-4 flex items-center gap-2"><Percent className="w-4 h-4" /> Reglas comerciales</h3>
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
                    <div>
                        <label className="fm-label flex items-center gap-1"><Percent className="w-3 h-3" /> Descuento %</label>
                        <input type="number" value={params.discount_pct} onChange={(e) => setParams({ ...params, discount_pct: parseFloat(e.target.value) || 0 })} className="fm-input" />
                    </div>
                    <div>
                        <label className="fm-label flex items-center gap-1"><Moon className="w-3 h-3" /> Recargo nocturno %</label>
                        <input type="number" value={params.night_surcharge_pct} onChange={(e) => setParams({ ...params, night_surcharge_pct: parseFloat(e.target.value) || 0 })} className="fm-input" />
                    </div>
                    <div>
                        <label className="fm-label flex items-center gap-1"><Calendar className="w-3 h-3" /> Recargo festivo %</label>
                        <input type="number" value={params.holiday_surcharge_pct} onChange={(e) => setParams({ ...params, holiday_surcharge_pct: parseFloat(e.target.value) || 0 })} className="fm-input" />
                    </div>
                    <div>
                        <label className="fm-label flex items-center gap-1"><Map className="w-3 h-3" /> $/km foráneo</label>
                        <input type="number" value={params.km_rate_foreign} onChange={(e) => setParams({ ...params, km_rate_foreign: parseFloat(e.target.value) || 0 })} className="fm-input" />
                    </div>
                    <div>
                        <label className="fm-label">SLA (min)</label>
                        <input type="number" value={params.sla_minutes} onChange={(e) => setParams({ ...params, sla_minutes: parseInt(e.target.value) || 0 })} className="fm-input" />
                    </div>
                    <div>
                        <label className="fm-label">Días crédito</label>
                        <input type="number" value={params.credit_days} onChange={(e) => setParams({ ...params, credit_days: parseInt(e.target.value) || 0 })} className="fm-input" />
                    </div>
                </div>
            </div>

            {/* Rates per service type */}
            <div className="fm-card overflow-hidden">
                <div className="p-5 border-b border-slate-200 flex items-center justify-between">
                    <div>
                        <h3 className="font-display font-bold text-base flex items-center gap-2"><TrendingUp className="w-4 h-4" /> Tarifario por tipo de servicio</h3>
                        <p className="text-xs text-slate-500 mt-1">Deja en blanco para usar tarifa base. Esta tarifa se aplica automáticamente al crear servicios para este cliente.</p>
                    </div>
                </div>
                <table className="w-full text-sm">
                    <thead>
                        <tr className="bg-slate-50 border-b border-slate-200">
                            {["Código", "Servicio", "Tarifa base", "Tarifa convenio", "Diferencia"].map((h) => (
                                <th key={h} className="text-left py-2.5 px-3 text-[10px] font-bold text-slate-500 uppercase tracking-wider">{h}</th>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {serviceTypes.map((st) => {
                            const current = rates[st.code];
                            const diff = current ? current - st.base_price : 0;
                            const diffPct = current ? (diff / st.base_price * 100) : 0;
                            return (
                                <tr key={st.code} className="border-b border-slate-100 hover:bg-slate-50">
                                    <td className="py-2 px-3 font-mono text-xs font-bold">{st.code}</td>
                                    <td className="py-2 px-3">{st.name}</td>
                                    <td className="py-2 px-3 text-right font-mono">{money(st.base_price)}</td>
                                    <td className="py-2 px-3 text-right">
                                        <input
                                            type="number"
                                            value={current || ""}
                                            onChange={(e) => updateRate(st.code, e.target.value)}
                                            placeholder={`${st.base_price}`}
                                            className="fm-input text-right font-mono w-32"
                                            data-testid={`rate-${st.code}`}
                                        />
                                    </td>
                                    <td className="py-2 px-3 text-right">
                                        {current && diff !== 0 && (
                                            <span className={`font-mono text-xs ${diff < 0 ? "text-red-600" : "text-emerald-600"}`}>
                                                {diff > 0 ? "+" : ""}{money(diff)} ({diffPct > 0 ? "+" : ""}{diffPct.toFixed(1)}%)
                                            </span>
                                        )}
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>

            <div className="flex items-center justify-end gap-3 sticky bottom-0 bg-slate-100 py-3">
                <button onClick={save} disabled={saving} className="fm-btn-accent" data-testid="save-rates-btn">
                    <Save className="w-3.5 h-3.5" />
                    {saving ? "Guardando..." : "Guardar tarifario"}
                </button>
            </div>
        </div>
    );
}
